"use client";

import type { FC } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { trackPutEdToWorkTap } from "@/src/hooks/onboarding/onboardingAnalytics";
import { PLAN_COPY } from "@components/OnboardingWizard/onboardingWizardCopy";
import { PlanFitnessImpactStrip } from "@components/OnboardingWizard/PlanOverviewModal/PlanFitnessImpactStrip";
import { PlanMoveRow } from "@components/OnboardingWizard/PlanOverviewModal/PlanMoveRow";
import { OnboardingEmphasisCtaButton } from "@components/OnboardingWizard/shared/OnboardingEmphasisCtaButton";
import type { OnboardingV3PlanImpact } from "@/src/hooks/onboarding/onboardingV3Types";
import type { GoalImpactData } from "@components/fitness/types";
import { SaveGoalCardGoalPreview } from "../SaveGoalCardGoalPreview";
import { normalizeSaveGoalTrackerSpecs } from "../saveGoalTrackerSpecs";
import { SAVE_GOAL_ACTIVATE_COPY } from "../saveGoalActivateCardCopy";
import type { PlanUnlockedMovesModalContentProps } from "./types";

const PLAN_IMPACT_DIMENSIONS = new Set<
  OnboardingV3PlanImpact["dimensions"][number]
>(["control", "safety", "growth"]);

/** SaveGoalArgs.impact → PlanFitnessImpactStrip（仅保留雷达三轴维度） */
const toPlanImpact = (
  impact: GoalImpactData | null | undefined,
): OnboardingV3PlanImpact | null | undefined => {
  if (impact == null) {
    return impact;
  }

  return {
    delta: impact.delta,
    dimensions: impact.dimensions.filter(
      (dimension): dimension is OnboardingV3PlanImpact["dimensions"][number] =>
        PLAN_IMPACT_DIMENSIONS.has(
          dimension as OnboardingV3PlanImpact["dimensions"][number],
        ),
    ),
  };
};

/**
 * Plan – Unlocked moves (Figma 96138:33274).
 */
export const PlanUnlockedMovesModalContent: FC<
  PlanUnlockedMovesModalContentProps
> = ({ args, mode, onActivate, onClose, isActivating = false }) => {
  const moves = normalizeSaveGoalTrackerSpecs(args.trackerSpecs);
  const isReadonly = mode === "readonly";
  const planImpact = toPlanImpact(args.impact);

  const handleActivate = () => {
    if (isReadonly || !onActivate) {
      return;
    }
    trackPutEdToWorkTap("put_ed_to_work");
    void onActivate();
  };

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-background_solid_neutral_secondary pt-safe"
      data-testid="plan-unlocked-moves-modal"
      data-figma-node="96138:33274"
      data-mode={mode}
    >
      <header className="flex shrink-0 items-center px-4 pb-3 pt-2">
        <button
          type="button"
          data-testid="plan-unlocked-moves-close"
          aria-label="Close"
          onClick={onClose}
          className="flex size-10 items-center justify-center rounded-full bg-background_solid_neutral_primary shadow-[0px_0px_32px_0px_rgba(0,0,0,0.08)]"
        >
          <Icon name="close" size={20} className="text-content_primary" />
        </button>
      </header>

      <div
        className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto overscroll-contain px-6 pb-4 pt-4"
        data-testid="plan-unlocked-moves-scroll"
      >
        <section
          className="flex flex-col gap-3"
          data-testid="plan-goal-section"
        >
          <h2 className="headline_regular_s text-content_primary">
            {PLAN_COPY.goalSectionTitle}
          </h2>
          <SaveGoalCardGoalPreview args={args} className="w-full" />
        </section>

        <PlanFitnessImpactStrip impact={planImpact} />

        <section
          className="flex flex-col gap-6"
          data-testid="plan-plan-section"
        >
          <div>
            <h2 className="headline_regular_s text-content_primary">
              {PLAN_COPY.planSectionTitle}
            </h2>
            <p className="body_m mt-0 text-content_secondary">
              {moves.length > 0
                ? SAVE_GOAL_ACTIVATE_COPY.movesBadge(moves.length)
                : PLAN_COPY.planSectionSubtitle}
            </p>
          </div>

          <div className="flex flex-col gap-3" data-testid="plan-moves-list">
            {moves.map((move, index) => (
              <PlanMoveRow
                key={move.id}
                number={index + 1}
                title={move.title}
                tag=""
                body={move.description}
                locked={false}
              />
            ))}
          </div>
        </section>
      </div>

      {!isReadonly ? (
        <div className="shrink-0 p-4" data-testid="plan-unlocked-moves-footer">
          <OnboardingEmphasisCtaButton
            testId="plan-unlocked-moves-activate-cta"
            label={SAVE_GOAL_ACTIVATE_COPY.activateCta}
            disabled={isActivating}
            loading={isActivating}
            sizeClassName="button_m"
            className={cn("rounded-lg")}
            onClick={handleActivate}
          />
        </div>
      ) : null}
    </div>
  );
};
